import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useArticle } from "@/lib/useArticles";

export default function StoryDetail() {
  const { slug } = useParams();
  const s = useArticle(slug);

  if (s === undefined) {
    return <div className="max-w-[1400px] mx-auto px-6 py-24 text-center" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>;
  }
  if (!s) return <Navigate to="/stories" replace />;

  return (
    <article data-testid="story-detail" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-16">
      <Link
        to="/stories"
        data-testid="back-to-stories"
        className="inline-flex items-center gap-2 text-sm mb-8"
        style={{ color: "var(--wofy-text-soft)" }}
      >
        <ArrowLeft className="w-4 h-4" /> Success Stories
      </Link>

      <header className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
        <div className={s.featured_image ? "lg:col-span-7" : "lg:col-span-12"}>
          {s.category && <div className="overline">{s.category.toUpperCase()}</div>}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-4">{s.title}</h1>
          {s.subtitle && (
            <p className="mt-6 text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "var(--wofy-text-soft)" }}>
              {s.subtitle}
            </p>
          )}
          {s.author_name && (
            <div className="overline mt-6">By {s.author_name}</div>
          )}
        </div>
        {s.featured_image && (
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
              <img src={s.featured_image} alt={s.title} className="w-full h-[380px] lg:h-[440px] object-cover" />
            </div>
          </div>
        )}
      </header>

      {(s.intro || s.lead) && (
        <div className="mt-16 grid lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-3"><div className="overline">Introduction</div></div>
          <div className="lg:col-span-9 space-y-5">
            {s.intro && <p className="text-lg leading-relaxed font-display">{s.intro}</p>}
            {s.lead && <p className="text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>{s.lead}</p>}
          </div>
        </div>
      )}

      {s.stats?.length > 0 && (
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-y py-10" style={{ borderColor: "var(--wofy-border)" }}>
          {s.stats.map((st, i) => (
            <div key={`${st.v}-${i}`}>
              <div className="font-display text-3xl md:text-4xl" style={{ color: "var(--wofy-primary)" }}>{st.k}</div>
              <div className="overline mt-2">{st.v}</div>
            </div>
          ))}
        </div>
      )}

      {s.sections?.length > 0 && (
        <div className="mt-16 space-y-14 md:space-y-16">
          {s.sections.map((sec, si) => (
            <section key={`${sec.heading}-${si}`} className="grid lg:grid-cols-12 gap-8 md:gap-12">
              <div className="lg:col-span-3">
                <h2 className="font-display text-2xl leading-tight">{sec.heading}</h2>
              </div>
              <div className="lg:col-span-9 space-y-5">
                {sec.body?.map((p, i) => (
                  <p key={`${si}-p-${i}`} className="text-base leading-relaxed">{p}</p>
                ))}
                {sec.list?.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {sec.list.map((li, i) => (
                      <li key={`${si}-l-${i}`} className="flex items-start gap-3 text-base leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--wofy-primary)" }} />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {sec.outro && (
                  <p className="text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>{sec.outro}</p>
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </article>
  );
}
